/**
 * Photographs held for one job, before they reach storage.
 *
 * A photo is kept as an object URL so the card can show what was actually
 * taken. `revoke` matters: object URLs are held by the document until released,
 * and an estimator working a full day would otherwise accumulate every shot of
 * every job in memory.
 */

import type { DownscalePlan } from './downscale.js';

export interface CapturedPhoto {
  /** Object URL for display. Valid until `revokePhoto` is called. */
  url: string;
  width: number;
  height: number;
  /** Size after downscale — what actually goes over the shop's connection. */
  bytes: number;
  /** What this photo will cost the model to read. */
  tokens: number;
  takenAt: string;
}

export function photoFrom(blob: Blob, plan: DownscalePlan, takenAt: string): CapturedPhoto {
  return {
    url: URL.createObjectURL(blob),
    width: plan.width,
    height: plan.height,
    bytes: blob.size,
    tokens: plan.tokens,
    takenAt,
  };
}

export function revokePhoto(photo: CapturedPhoto | undefined): void {
  if (photo) URL.revokeObjectURL(photo.url);
}

/** Bytes as a shop would read them — a workshop cares about "is this a lot". */
export function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
